import { DEFAULT_LANGUAGE, LANGUAGES, SUPPORTED_LANGUAGE_CODES } from "../i18n/languages";

export const BLOG_LANGUAGES = SUPPORTED_LANGUAGE_CODES;
export const DEFAULT_BLOG_LANGUAGE = DEFAULT_LANGUAGE;

export const BLOG_LANGUAGE_OPTIONS = LANGUAGES.map((lang) => ({
    code: lang.code,
    label: lang.label,
    short: lang.short,
}));

export function emptyLocalizedString() {
    return BLOG_LANGUAGES.reduce((acc, code) => {
        acc[code] = "";
        return acc;
    }, {});
}

/**
 * Normalize API/legacy values into { en, tr, zh }.
 * Plain strings are treated as English.
 */
export function normalizeLocalizedField(value) {
    const empty = emptyLocalizedString();
    if (value == null) return empty;

    if (typeof value === "string") {
        return { ...empty, [DEFAULT_BLOG_LANGUAGE]: value.trim() };
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        const next = { ...empty };
        for (const code of BLOG_LANGUAGES) {
            if (value[code] != null) next[code] = String(value[code]).trim();
        }
        if (!next.en && typeof value.value === "string") {
            next.en = value.value.trim();
        }
        return next;
    }

    return empty;
}

export function resolveLocalizedValue(field, lang = DEFAULT_BLOG_LANGUAGE) {
    const map = normalizeLocalizedField(field);
    const code = BLOG_LANGUAGES.includes(lang) ? lang : DEFAULT_BLOG_LANGUAGE;
    return map[code] || map[DEFAULT_BLOG_LANGUAGE] || "";
}

export function getAvailableBlogLanguages(blog) {
    const title = normalizeLocalizedField(blog?.title);
    const content = normalizeLocalizedField(blog?.content);
    return BLOG_LANGUAGES.filter((code) => Boolean(title[code]) && Boolean(content[code]));
}

export function localizeBlog(blog, lang = DEFAULT_BLOG_LANGUAGE) {
    if (!blog) return null;
    const code = BLOG_LANGUAGES.includes(lang) ? lang : DEFAULT_BLOG_LANGUAGE;
    const fromApi = blog.localized && blog.language === code ? blog.localized : null;

    return {
        ...blog,
        language: code,
        title: fromApi?.title ?? resolveLocalizedValue(blog.title, code),
        intro: fromApi?.intro ?? resolveLocalizedValue(blog.intro, code),
        content: fromApi?.content ?? resolveLocalizedValue(blog.content, code),
        metaTitle: fromApi?.metaTitle ?? resolveLocalizedValue(blog.metaTitle, code),
        metaDescription:
            fromApi?.metaDescription ?? resolveLocalizedValue(blog.metaDescription, code),
        metaKeywords:
            fromApi?.metaKeywords ?? resolveLocalizedValue(blog.metaKeywords, code),
        availableLanguages: Array.isArray(blog.availableLanguages)
            ? blog.availableLanguages
            : getAvailableBlogLanguages(blog),
    };
}

/** Search/sort helper: flatten all language strings. */
export function localizedFieldSearchText(field) {
    const map = normalizeLocalizedField(field);
    return BLOG_LANGUAGES.map((code) => map[code]).filter(Boolean).join(" ");
}
